// The invoice sheet in Blueprint (InvoiceSheet.tsx's, BL-189): one finalized invoice over the place it
// was opened from, at the old sheet's width (ui-web's dialog layer, size fill). Its number, who it's for
// and when it was issued and is due, and its state; its figures (total, paid, balance due); then the
// invoice on the left (how much is paid, its lines, its totals, what was just done and what can be done
// next) and the money in, deposits, the customer's signature and who it bills on the right. Drawn with
// catalogued parts: Layer, Label, Figures, KeyValue, Meter, LineTable, Totals, RowList, Section, RecordLayout,
// Notice, Button and Link. What it does next (send, a card being checked, record a payment, write off,
// the signature) is the money parts the job page and the customer record share, drawn in Blueprint on
// catalogued parts too (BL-189); their sheets go strict with those pages. Riverstone keeps
// InvoiceSheet.tsx's own.
import { useState } from 'react';
import {
  Button,
  Figures,
  KeyValue,
  Label,
  Layer,
  LineTable,
  Link,
  Meter,
  Notice,
  RowList,
  Skeleton,
  SkeletonGroup,
  Totals,
  type FigureItem,
  type KeyValueItem,
  type LabelKind,
  type LineRow,
  type Row,
  type TotalsRow,
} from '@flowology/ui-web/fl';
import { RecordLayout, Section } from '@flowology/ui-web/patterns';
import { dateText } from '@flowology/kernel';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { money, todayIn } from '../books/model.ts';
import { CardBeingChecked, WriteOff } from '../history/MoneyActions.tsx';
import { methodWord, moneyRights } from '../history/money-model.ts';
import { openJob } from '../job/job-page.ts';
import { lineTagsText } from '../job/job-model.ts';
import { InvoiceActions } from '../review/InvoiceActions.tsx';
import { RecordPayment } from '../books/RecordPayment.tsx';
import { SignedForWork } from '../shared/SignedForWork.tsx';
import {
  adjustedWord,
  afterRefunds,
  appliedWord,
  draftNote,
  dueText,
  qtyText,
  rateText,
  refundTaxText,
  restOfPayment,
  statusOf,
  type InvoiceSheetData,
} from './invoice-model.ts';
import { useInvoice, type Refunds } from './invoice-state.ts';
import './invoice-layer.css';

/** The status's tone as a Label: overdue is a problem, partly paid waits, the rest are settled words. */
const STATUS_KIND: Record<ReturnType<typeof statusOf>['tone'], LabelKind> = {
  bad: 'bad',
  warn: 'wait',
  ok: 'muted',
  '': 'muted',
};

export function InvoiceLayer({
  invoiceId,
  held,
  timeZone,
  onClose,
  onOpenCustomer,
  onChanged,
}: {
  invoiceId: string;
  held: readonly string[];
  timeZone: string;
  onClose: () => void;
  onOpenCustomer: (customerId: string) => void;
  onChanged?: () => void;
}) {
  const { inv, refunds, depositNotes, error, said, setSaid, moved } = useInvoice(invoiceId, onChanged);
  // the invoice and its payments are the import source's while the company mirrors it (BL-088)
  const mirroring = useMirror();
  const today = todayIn(timeZone);
  const label = inv ? `Invoice ${inv.number}` : 'Invoice';
  return (
    // Escape and Close shut it, as the old sheet; a click beside it never does (a payment half typed)
    <Layer level="dialog" size="fill" label={label} onClose={onClose} isDismissable={false}>
      {inv ? (
        <InvoiceBody
          inv={inv}
          refunds={refunds}
          depositNotes={depositNotes}
          said={said}
          setSaid={setSaid}
          moved={moved}
          held={held}
          mirroring={mirroring}
          today={today}
          timeZone={timeZone}
          onClose={onClose}
          onOpenCustomer={onOpenCustomer}
        />
      ) : (
        <div className="bp-inv">
          <h2 className="bp-inv-title">Invoice</h2>
          {error ? (
            <p className="bp-inv-bad" role="alert">
              {error}
            </p>
          ) : (
            <SkeletonGroup label="Loading the invoice">
              <Skeleton shape="title" width="long" />
              <Skeleton shape="row" width="full" />
              <Skeleton shape="row" width="full" />
            </SkeletonGroup>
          )}
        </div>
      )}
    </Layer>
  );
}

function InvoiceBody({
  inv,
  refunds,
  depositNotes,
  said,
  setSaid,
  moved,
  held,
  mirroring,
  today,
  timeZone,
  onClose,
  onOpenCustomer,
}: {
  inv: InvoiceSheetData;
  refunds: Refunds | null;
  depositNotes: string[];
  said: string | null;
  setSaid: (words: string | null) => void;
  moved: (words?: string) => Promise<void>;
  held: readonly string[];
  mirroring: boolean;
  today: string;
  timeZone: string;
  onClose: () => void;
  onOpenCustomer: (customerId: string) => void;
}) {
  const [writingOff, setWritingOff] = useState(false);
  const [paying, setPaying] = useState(false);
  const rights = moneyRights(held, mirroring);
  // a local date (issued, refunded) or an instant (a payment), each in the company's day
  const day = (d: string) => dateText(d, timeZone, { year: false, now: today });
  const status = statusOf(inv, today);
  // what it bills and what was kept, less what was given back (QA-713)
  const after = afterRefunds(inv);
  const refundTax = refundTaxText(inv);
  const paid = after.paidCents;
  const due = dueText(inv, today);

  const figures: FigureItem[] = [
    {
      label: 'Total',
      value: money(after.totalCents),
      sub:
        after.refundedCents > 0 || after.taxes.length > 0 ? (
          <>
            {after.refundedCents > 0 && (
              <span className="bp-inv-block">
                {money(inv.totalCents)} less {money(after.refundedCents)} refunded
              </span>
            )}
            {after.taxes.length > 0 && (
              <span className="bp-inv-block">
                {after.taxes.map((t) => `${t.code} ${money(t.taxCents)}`).join(', ')}
              </span>
            )}
          </>
        ) : undefined,
    },
    {
      label: 'Paid',
      value: money(paid),
      sub:
        after.refundedCents > 0
          ? `${money(paid + after.refundedCents)} in, ${money(after.refundedCents)} given back`
          : paid > 0
            ? 'so far'
            : 'nothing yet',
    },
    {
      label: 'Balance due',
      value: money(inv.balanceCents),
      bad: status.tone === 'bad',
      ...(due ? { sub: due } : {}),
    },
  ];

  const lines: LineRow[] = inv.lines.map((l) => ({
    id: l.id,
    cells: {
      what: (
        <>
          {l.description}
          {/* the import source's item code and the line's technician (history fix 2) */}
          {lineTagsText(l) && <span className="bp-inv-quiet"> · {lineTagsText(l)}</span>}
          {(l.fromEstimate || l.overrideReason) && (
            <span className="bp-inv-quiet bp-inv-block">
              {l.fromEstimate ? 'From the estimate' : ''}
              {l.fromEstimate && l.overrideReason ? ' · ' : ''}
              {l.overrideReason ?? ''}
            </span>
          )}
        </>
      ),
      qty: qtyText(l.quantity),
      price: money(l.unitPriceCents),
      total: money(l.totalCents),
    },
  }));

  const totals: TotalsRow[] = [
    { label: 'Subtotal', value: money(inv.subtotalCents) },
    ...inv.taxes.map((t) => ({
      label: `${t.code} ${rateText(t.rate)} on ${money(t.baseCents)}`,
      value: money(t.taxCents),
    })),
    ...(inv.taxExempt ? [{ label: inv.taxExempt, value: money(0) }] : []),
    { label: 'Total', value: money(inv.totalCents), kind: 'strong' as const },
    ...(after.refundedCents > 0
      ? [
          {
            label: (
              <>
                Refunded, a credit note
                {refundTax && <span className="bp-inv-quiet bp-inv-block">Tax back: {refundTax}</span>}
              </>
            ),
            value: money(-after.refundedCents),
          },
          { label: 'Total after refunds', value: money(after.totalCents), kind: 'strong' as const },
        ]
      : []),
    ...(inv.writtenOffCents > 0 ? [{ label: 'Written off', value: money(inv.writtenOffCents) }] : []),
    // what credit notes took off it: settled, not paid (history fix 2)
    ...((inv.adjustedCents ?? 0) > 0 ? [{ label: 'Adjusted', value: money(-inv.adjustedCents!) }] : []),
    { label: after.refundedCents > 0 ? 'Paid, less what was given back' : 'Paid', value: money(paid) },
    { label: 'Balance', value: money(inv.balanceCents), kind: 'due' },
  ];

  const moneyInTitle = inv.applied.some((a) => adjustedWord(a, inv.number, day))
    ? 'Money in and adjustments'
    : 'Money in';
  const moneyIn: Row[] = [
    ...inv.applied.map((a, i): Row => {
      // a credit note on this invoice names itself and takes its amount off (history fix 2)
      const adjusted = adjustedWord(a, inv.number, day);
      // a payment more than this invoice took says where the rest went (QA-716)
      const rest = restOfPayment(a);
      return {
        id: `${a.kind}-${a.at}-${i}`,
        name: adjusted ?? `${day(a.at)} · ${appliedWord(a)}`,
        ...(!adjusted && rest ? { after: { text: ` (${rest})`, size: 'sm' as const } } : {}),
        end: { figure: money(adjusted ? -a.amountCents : a.amountCents) },
      };
    }),
    ...(refunds?.refunds ?? []).map((r): Row => ({
      id: r.refundId,
      name: `${day(r.refundedOn)} · Refunded by ${methodWord(r.method)}: ${r.reason}`,
      end: { figure: money(-r.amountCents) },
    })),
  ];

  const waits = draftNote(inv);
  const canPay = rights.record && !paying && !inv.cardBeingChecked;
  // only a finalised invoice is written off (invoices.writeOff), never a draft (QA-804)
  const canWriteOff = rights.writeOff && inv.status !== 'draft';
  const owing = inv.balanceCents > 0 && inv.status !== 'void';

  const bills: KeyValueItem[] = [
    {
      label: 'Customer',
      value: <Link onPress={() => onOpenCustomer(inv.customerId)}>{inv.customerName}</Link>,
    },
    ...(inv.jobId && inv.jobNumber
      ? [
          {
            label: 'Job',
            value: (
              <Link
                onPress={() => {
                  onClose();
                  openJob(inv.jobId!);
                }}
              >
                Job #{inv.jobNumber}
              </Link>
            ),
          },
        ]
      : []),
    ...(inv.issuedOn ? [{ label: 'Issued', value: day(inv.issuedOn) }] : []),
    { label: 'PO', value: inv.poNumber ?? 'None' },
  ];

  const main = (
    <>
      <Section title="The invoice" level={3}>
        {after.totalCents > 0 && (
          <div className="bp-inv-paid">
            <Meter value={paid} max={after.totalCents} label="Paid so far" fill="hatch" />
            <p className="bp-inv-quiet">
              {paid > 0 ? `Paid ${money(paid)}` : 'Nothing paid yet'}
              {after.refundedCents > 0 ? ` after ${money(after.refundedCents)} given back` : ''}; still to pay{' '}
              {money(Math.max(0, inv.balanceCents))}.
            </p>
          </div>
        )}
        <LineTable
          label={`${inv.number}’s lines`}
          columns={[
            { id: 'what', label: 'Description' },
            { id: 'qty', label: 'Qty', align: 'end' },
            { id: 'price', label: 'Price', align: 'end' },
            { id: 'total', label: 'Total', align: 'end' },
          ]}
          rows={lines}
        />
        <Totals label={`${inv.number}’s totals`} rows={totals} />
      </Section>
      {said && <Notice tone="done" title={said} />}
      <div className="bp-inv-acts">
        {waits && <p className="bp-inv-quiet">{waits}</p>}
        {inv.status !== 'draft' && (
          <InvoiceActions
            invoice={inv}
            // one primary for the invoice: Send is secondary whenever something follows it (a card
            // being checked, Record a payment or Write off…), as the old sheet drew it
            sendVariant={inv.cardBeingChecked || (owing && (canPay || canWriteOff)) ? 'secondary' : 'primary'}
            sendWord={
              inv.status === 'sent' || inv.status === 'partially_paid' ? 'Send again' : 'Send to customer'
            }
            onSent={() => void moved('Sent.')}
          />
        )}
        {inv.cardBeingChecked && (
          <CardBeingChecked
            check={inv.cardBeingChecked}
            timeZone={timeZone}
            canAct={rights.record}
            onDone={(m) => void moved(m)}
          />
        )}
        {owing && (canPay || canWriteOff) && (
          <div className="bp-inv-row">
            {canPay && (
              <Button variant="primary" onPress={() => setPaying(true)}>
                Record a payment
              </Button>
            )}
            {canWriteOff && <Button onPress={() => setWritingOff(true)}>Write off…</Button>}
          </div>
        )}
      </div>
      {paying && (
        // books' shared form (F-07): this invoice first, then the customer's others oldest first
        <div className="books">
          <RecordPayment
            customerId={inv.customerId}
            customerName={inv.customerName}
            firstInvoiceId={inv.id}
            timeZone={timeZone}
            dates={held.includes('invoices.edit')}
            toast={setSaid}
            onDone={(recorded) => {
              setPaying(false);
              if (recorded) void moved();
            }}
          />
        </div>
      )}
      {writingOff && (
        <WriteOff
          invoice={inv}
          onClose={() => setWritingOff(false)}
          onDone={(m) => {
            setWritingOff(false);
            void moved(m);
          }}
        />
      )}
    </>
  );

  const side = (
    <>
      <Section title={moneyInTitle} level={3}>
        {moneyIn.length > 0 ? (
          <RowList label={moneyInTitle} rows={moneyIn} />
        ) : (
          <p className="bp-inv-quiet">Nothing paid yet.</p>
        )}
      </Section>
      {depositNotes.length > 0 && (
        <Section title="Deposits" level={3}>
          {depositNotes.map((t) => (
            <p key={t} className="bp-inv-quiet">
              {t}
            </p>
          ))}
        </Section>
      )}
      {/* the customer's signature accepting the work, who and when, and the pad (QA-727) */}
      {inv.signature && <SignedForWork signatures={[inv.signature]} timeZone={timeZone} />}
      <Section title="Who it bills" level={3}>
        <KeyValue items={bills} />
      </Section>
    </>
  );

  return (
    <div className="bp-inv">
      <header className="bp-inv-head">
        <div>
          <p className="bp-inv-kicker">
            {inv.customerName}
            {inv.jobNumber ? `, job #${inv.jobNumber}` : ''}
            {/* the import source's while mirroring: sent, paid and written off there (history fix 2) */}
            {inv.imported && mirroring ? '. From ServiceTitan' : ''}
          </p>
          <h2 className="bp-inv-title">Invoice {inv.number}</h2>
          {(inv.issuedOn || inv.dueOn) && (
            <p className="bp-inv-quiet">
              {inv.issuedOn ? `Issued ${day(inv.issuedOn)}` : ''}
              {inv.issuedOn && inv.dueOn ? ', ' : ''}
              {inv.dueOn ? `${inv.issuedOn ? 'due' : 'Due'} ${day(inv.dueOn)}` : ''}.
            </p>
          )}
          {mirroring && moneyRights(held).record && <p className="bp-inv-quiet">{MIRROR_LINE}</p>}
        </div>
        <Label kind={STATUS_KIND[status.tone]}>{status.word}</Label>
      </header>
      <Figures label="The invoice in figures" items={figures} />
      <RecordLayout underFigures sideLabel="Money in and who it bills" main={main} side={side} />
    </div>
  );
}
